import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

interface SEOData {
  title: string;
  description: string;
  image?: string;
  keywords?: string;
  type?: string;
  noindex?: boolean;
}

export const useSEO = (data: SEOData) => {
  const location = useLocation();
  
  useEffect(() => {
    // Reset to defaults when navigating away
    return () => {
      document.title = '모아프렌즈 - 멤버십 플랫폼';
    };
  }, [location.pathname]);
  
  // Generate URL for current page
  const getCanonicalUrl = () => {
    const baseUrl = process.env.REACT_APP_BASE_URL || 'https://moafriends.com';
    return `${baseUrl}${location.pathname}`;
  };
  
  // Generate keywords from title and description if not provided
  const generateKeywords = (title: string, description: string, customKeywords?: string) => {
    if (customKeywords) return customKeywords;
    
    const baseKeywords = ['모아프렌즈', '멤버십', '구독', '혜택', '브랜드'];
    const titleWords = title.split(' ').filter(word => word.length > 2);
    const descWords = description.split(' ').filter(word => word.length > 3).slice(0, 5);
    
    return [...new Set([...baseKeywords, ...titleWords, ...descWords])].join(', ');
  };
  
  // Format price for structured data
  const formatPrice = (price: number | string) => {
    return typeof price === 'string' ? price.replace(/[^0-9]/g, '') : price.toString();
  };
  
  // Generate breadcrumb for structured data
  const generateBreadcrumb = () => {
    const paths = location.pathname.split('/').filter(Boolean);
    const breadcrumb = [
      { name: '홈', url: '/' }
    ];
    
    let currentPath = '';
    paths.forEach((path, index) => {
      currentPath += `/${path}`;
      let name = path;
      
      // Convert path to readable name
      switch (path) {
        case 'brand':
          name = '브랜드';
          break;
        case 'membership':
          name = '멤버십';
          break;
        case 'product':
          name = '상품';
          break;
        case 'voucher':
          name = '바우처';
          break;
        default:
          if (index === paths.length - 1 && !isNaN(Number(path))) {
            // Skip ID in breadcrumb
            return;
          }
      }
      
      breadcrumb.push({
        name: name.charAt(0).toUpperCase() + name.slice(1),
        url: currentPath
      });
    });
    
    return breadcrumb;
  };
  
  return {
    canonicalUrl: getCanonicalUrl(),
    keywords: generateKeywords(data.title, data.description, data.keywords),
    breadcrumb: generateBreadcrumb(),
    formatPrice
  };
};